import { Button, ButtonColor, ButtonSize, TextInput } from '@code/innovance-react-ui-kit';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useYupResolver from 'src/front/common/hooks/useYupResolver';
import {
  formatAlphaNumeric,
  formatAlphaNumericCyrillicSpecialCharacters,
  formatAlphaNumericCyrillicSpecialCharactersWithDot,
} from 'src/front/common/utils/formUtils';
import { CustomerSearchSchema } from 'src/front/common/utils/validationSchemas';

import { ButtonRow, FormElement, FormRow, ResetButton } from 'src/front/components/FormStyles';
import Datepicker from './Datepicker';

export interface SearchFormData {
  customerSurname: string;
  customerName: string;
  customerSecondName: string;
  birthDate: Date | null;
  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
}

export const customerSearchDefaultValues = {
  customerName: '',
  customerSurname: '',
  birthDate: null,
  customerSecondName: '',
  identityDocumentSerialCode: '',
  identityDocumentSerialNumber: '',
};

interface Props {
  onSubmit: (formData: SearchFormData) => void;
  formValues?: SearchFormData;
  disabled?: boolean;
}

const CustomerSearchForm: FC<Props> = ({ onSubmit, formValues, disabled = false }) => {
  const { t } = useTranslation();

  const resolver = useYupResolver(CustomerSearchSchema, {
    todayMaxControl: true,
  });

  const { errors, handleSubmit, control, reset, formState } = useForm<SearchFormData>({
    defaultValues: { ...customerSearchDefaultValues, ...formValues },
    resolver,
  });

  const onReset = (): void => reset();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormRow>
        <FormElement>
          <Controller
            control={control}
            name="customerSurname"
            render={({ onChange, value }) => (
              <TextInput
                disabled={disabled}
                errorMessage={t(errors?.customerSurname?.message as string)}
                formatter={formatAlphaNumericCyrillicSpecialCharacters}
                label={t('cardEntry_customerInfo_surname_label')}
                maxLength={60}
                name="customerSurname"
                onChange={onChange}
                value={value}
                required
              />
            )}
          />
        </FormElement>
        <FormElement>
          <Controller
            control={control}
            name="customerName"
            render={({ onChange, value }) => (
              <TextInput
                disabled={disabled}
                errorMessage={t(errors?.customerName?.message as string)}
                formatter={formatAlphaNumericCyrillicSpecialCharacters}
                label={t('cardEntry_customerInfo_name_label')}
                maxLength={40}
                name="customerName"
                onChange={onChange}
                value={value}
                required
              />
            )}
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Controller
            control={control}
            name="customerSecondName"
            render={({ onChange, value }) => (
              <TextInput
                disabled={disabled}
                errorMessage={t(errors?.customerSecondName?.message as string)}
                formatter={formatAlphaNumericCyrillicSpecialCharactersWithDot}
                label={t('cardEntry_customerInfo_middleName_label')}
                maxLength={30}
                name="customerSecondName"
                onChange={onChange}
                value={value}
              />
            )}
          />
        </FormElement>
        <FormElement>
          <Controller
            control={control}
            name="birthDate"
            render={({ onChange, value }) => (
              <Datepicker
                disabled={disabled}
                errorMessage={t(errors?.birthDate?.message as string)}
                inputLabel={`${t('cardEntry_customerInfo_birthdate_label')} *`}
                onChange={onChange}
                value={value}
              />
            )}
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Controller
            control={control}
            name="identityDocumentSerialCode"
            render={({ onChange, value }) => (
              <TextInput
                disabled={disabled}
                errorMessage={t(errors?.identityDocumentSerialCode?.message as string)}
                formatter={formatAlphaNumeric}
                label={t('general_documentSeries_label')}
                maxLength={4}
                name="identityDocumentSerialCode"
                onChange={onChange}
                value={value}
              />
            )}
          />
        </FormElement>
        <FormElement>
          <Controller
            control={control}
            name="identityDocumentSerialNumber"
            render={({ onChange, value }) => (
              <TextInput
                disabled={disabled}
                errorMessage={t(errors?.identityDocumentSerialNumber?.message as string)}
                formatter={formatAlphaNumeric}
                label={t('general_documentNumber_label')}
                maxLength={21}
                name="identityDocumentSerialNumber"
                onChange={onChange}
                value={value}
              />
            )}
          />
        </FormElement>
      </FormRow>
      <ButtonRow>
        {formState.isSubmitSuccessful ? (
          <ResetButton
            color={ButtonColor.Secondary}
            disabled={disabled}
            onClick={onReset}
            size={ButtonSize.Medium}
            text={t('general_components_reset_button')}
            type="reset"
          />
        ) : null}
        <Button
          color={ButtonColor.Primary}
          disabled={formState.isSubmitting || disabled}
          size={ButtonSize.Medium}
          text={t('general_components_search_button')}
          type="submit"
        />
      </ButtonRow>
    </form>
  );
};

export default CustomerSearchForm;
